import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { queryStore } from "../../../../../packages/api/src/queryStore";
import { hubMutations } from "@surgia/api/src/queryStore/hub";
import { useTranslation } from "react-i18next";
import mailIcon from "@surgia/ui/icons/popup/email.svg"; // adjust path as needed
import { countries } from "../../../../../packages/contract/constants/countryCodes";
import sortIcon from "@surgia/ui/icons/table/sort.svg"; // for dropdown arrow
import { useState, useRef } from "react";
import Button from "@surgia/ui/components/button";
import { RequestData } from "../../../../../packages/contract/dtos";
import { zodResolver } from "@hookform/resolvers/zod";
import { registerSchema, RegisterFormValues } from "./validation"; // adjust path as needed
import { useNavigate } from "react-router-dom";
import { Listbox } from "@headlessui/react";

const institutionTypes = [
  { value: "HOSPITAL", labelKey: "shared.entityType.HOSPITAL" },
  { value: "POLY_CLINIC", labelKey: "shared.entityType.POLY_CLINIC" },
  { value: "OTHER", labelKey: "shared.entityType.OTHER" },
];

export function RegisterForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(countries[1]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    reset: resetForm,
    formState: { errors },
    watch,
    clearErrors,
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema(t)),
    mode: "onChange",
    defaultValues: {
      name: "",
      type: "",
      city_id: "",
      contact_person_first_name: "",
      contact_person_last_name: "",
      contact_person_email: "",
      contact_person_mobile: "",
      mobile_number_prefix: "+966",
    },
  });

  const { data: citiesResponse } = useQuery({
    ...queryStore.shared.lookup.queries.cities({
      sort: "name,asc",
      size: 100,
      page: 0,
      country_id: 191,
    }),
    staleTime: 600000,
  });

  // Extract cities from the response
  const cities = citiesResponse?.data;

  // Watch form values for listbox selections
  const selectedType = watch("type");

  // Find the selected institution type object
  const selectedInstitutionType = institutionTypes.find(
    (type) => type.value === selectedType
  );

  // Use the new request mutation from query store
  const createRequestMutation = useMutation(hubMutations.request.create());

  const onSubmit = (data: RegisterFormValues) => {
    if (!navigator.onLine) {
      toast.error(t("hub.register.offlineError"));
      return;
    }

    // Manual phone validation
    const country = countries.find(
      (c: any) => c.dial === data.mobile_number_prefix
    );
    if (
      country &&
      !country.regex.test(data.contact_person_mobile || "") &&
      (data.contact_person_mobile?.length ?? 0) > 0
    ) {
      setError("contact_person_mobile", {
        type: "manual",
        message: t("shared.branchForm.invalidPhone"),
      });
      return;
    }

    const phoneNumber = data.contact_person_mobile;
    const prefix = data.mobile_number_prefix;
    const cleanNumber = phoneNumber.replace(/^\+/, "");

    // Create the final data object with the formatted phone number
    const formattedData: RequestData = {
      name: data.name,
      type: data.type!,
      city_id: parseInt(data.city_id!),
      contact_person_first_name: data.contact_person_first_name,
      contact_person_last_name: data.contact_person_last_name,
      contact_person_email: data.contact_person_email,
      contact_person_mobile: prefix + cleanNumber,
      mobile_number_prefix: prefix,
      country_id: "191",
    };

    createRequestMutation.mutate(formattedData, {
      onSuccess: () => {
        resetForm();
        toast.success(t("hub.register.successMessage"));
        navigate("/login");
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.errorCodeDescription ||
          "An error occurred";
        if (errorMessage.includes("name")) {
          toast.error(t("hub.register.nameExists"));
        } else if (errorMessage.includes("email")) {
          toast.error(t("hub.register.emailExists"));
        } else if (errorMessage.includes("mobile")) {
          toast.error(t("hub.register.mobileExists"));
        }
      },
    });
  };
  return (
    <form className="grid grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
      {/* Institution name */}
      <div className="col-span-2">
        <label className="block mb-1 text-[14px] ">
          {t("hub.formFields.clinicHospitalName")}{" "}
          <span className="text-red-600">*</span>
        </label>
        <input
          {...register("name")}
          className="w-full border rounded-lg px-4 py-2"
          placeholder={t("hub.placeholders.clinicHospitalName")}
        />
        {errors.name && (
          <p className="text-red-500 text-xs">{errors.name.message}</p>
        )}
      </div>

      {/* Institution type - Listbox */}
      <div className="col-span-2">
        <label className="block mb-1 text-[14px] ">
          {t("hub.formFields.clinicHospitalType")}{" "}
          <span className="text-red-600">*</span>
        </label>
        <Listbox
          value={selectedInstitutionType || null}
          onChange={(selectedType) => {
            setValue("type", selectedType?.value || "");
            clearErrors("type");
          }}
        >
          <div className="relative">
            <Listbox.Button className="w-full border rounded-lg px-4 py-2 text-left bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500">
              <span className="block truncate">
                {selectedInstitutionType
                  ? t(selectedInstitutionType.labelKey)
                  : t("hub.placeholders.clinicHospitalType")}
              </span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </Listbox.Button>

            <Listbox.Options className="absolute z-[1000] mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
              {institutionTypes.map((type) => (
                <Listbox.Option
                  key={type.value}
                  value={type}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                      >
                        {t(type.labelKey)}
                      </span>
                      {selected ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                          <svg
                            className="w-5 h-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </Listbox>
        {errors.type && (
          <p className="text-red-500 text-xs">{errors.type.message}</p>
        )}
      </div>

      {/* City */}
      <div className="col-span-2">
        <label className="block mb-1 text-[14px] ">
          {t("hub.formFields.city")} <span className="text-red-600">*</span>
        </label>
        <Listbox
          value={
            cities?.find(
              (city) => String(city.id) === String(watch("city_id"))
            ) || null
          }
          onChange={(city) => {
            setValue("city_id", city?.id?.toString() || "");
            clearErrors("city_id");
          }}
        >
          <div className="relative">
            <Listbox.Button className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300 bg-white text-gray-500">
              <span className="block truncate text-left">
                {cities?.find(
                  (city) => String(city.id) === String(watch("city_id"))
                )?.name || t("hub.placeholders.city")}
              </span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </Listbox.Button>

            <Listbox.Options className="absolute z-[1000] mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
              {cities?.map((city) => (
                <Listbox.Option
                  key={city.id}
                  value={city}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                      >
                        {city.name}
                      </span>
                      {selected ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                          <svg
                            className="w-5 h-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </Listbox>
        {errors.city_id && (
          <p className="text-red-500 text-xs">{errors.city_id.message}</p>
        )}
      </div>

      {/* Contact person name */}
      <div>
        <label className="block mb-1 text-[14px] ">
          {t("hub.formFields.firstName")}{" "}
          <span className="text-red-600">*</span>
        </label>
        <input
          {...register("contact_person_first_name")}
          className="w-full border rounded-lg px-4 py-2"
          placeholder={t("hub.placeholders.firstName")}
        />
        {errors.contact_person_first_name && (
          <p className="text-red-500 text-xs">
            {errors.contact_person_first_name.message}
          </p>
        )}
      </div>
      <div>
        <label className="block mb-1 text-[14px] ">&nbsp;</label>
        <input
          {...register("contact_person_last_name")}
          className="w-full border rounded-lg px-4 py-2"
          placeholder={t("hub.placeholders.lastName")}
        />
        {errors.contact_person_last_name && (
          <p className="text-red-500 text-xs">
            {errors.contact_person_last_name.message}
          </p>
        )}
      </div>

      {/* Contact person Email */}
      <div className="col-span-2">
        <label className="block mb-1 text-[14px] ">
          {t("hub.formFields.email")} <span className="text-red-600">*</span>
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <img src={mailIcon} alt="Mail" className="w-5 h-5" />
          </span>
          <input
            {...register("contact_person_email")}
            className="w-full border rounded-lg px-4 py-2 pl-10"
            placeholder={t("hub.placeholders.email")}
            type="email"
          />
        </div>
        {errors.contact_person_email && (
          <p className="text-red-500 text-xs">
            {errors.contact_person_email.message}
          </p>
        )}
      </div>

      {/* Contact person number */}
      <div className="col-span-2">
        <label className="block mb-1 text-[14px] ">
          {t("hub.register.mobile")} <span className="text-red-600">*</span>
        </label>
        <div className="flex focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-gray-300 rounded-lg">
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className="flex items-center border border-r-0 rounded-l-lg px-3 py-2 bg-white min-w-[60px] focus:outline-none"
              onClick={() => setShowDropdown((v) => !v)}
              tabIndex={0}
            >
              <span className="text-xl">{selectedCountry.flag}</span>
              <span className="ml-2">
                <img src={sortIcon} alt="Sort" className="w-4 h-4" />
              </span>
            </button>
            {showDropdown && (
              <div
                className="absolute left-0 bottom-full mb-1 bg-white border rounded-lg shadow-lg z-10"
                style={{ minWidth: "100%", width: "max-content" }}
              >
                {countries.map((country: any) => (
                  <div
                    key={country.code}
                    className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                      selectedCountry.dial === country.dial ? "bg-gray-100" : ""
                    }`}
                    onClick={() => {
                      setSelectedCountry(country);
                      setValue("mobile_number_prefix", country.dial);
                      setShowDropdown(false);
                    }}
                  >
                    <span className="mr-2 text-xl">{country.flag}</span>
                    <span className="mr-2 text-sm text-gray-700">
                      {t(`shared.branchForm.countries.${country.code}`)}
                    </span>
                    <span className="ml-auto text-gray-500 text-xs">
                      {country.dial}
                    </span>
                    {selectedCountry.dial === country.dial && (
                      <span className="ml-6 flex items-center">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <input
            {...register("contact_person_mobile")}
            className="w-full border-t border-b border-r rounded-r-lg px-4 py-2 focus:outline-none"
            placeholder={t("hub.placeholders.mobile")}
            type="tel"
          />
        </div>
        {errors.contact_person_mobile && (
          <p className="text-red-500 text-xs">
            {errors.contact_person_mobile.message}
          </p>
        )}
      </div>

      <Button
        variant="purple"
        className="col-span-2 py-3 bg-[#7144FF] hover:bg-purple-700 text-white"
        type="submit"
        disabled={createRequestMutation.isPending}
      >
        {createRequestMutation.isPending
          ? t("hub.register.submitting")
          : t("hub.register.submit")}
      </Button>
    </form>
  );
}
